import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyQuery, useQuery } from "@apollo/client";

import { ContentContainer } from "../../../components/layout";
import LockerTable from "./components/locker-table";
import {
  Button,
  LinearProgressBar,
  Modal,
  Select,
} from "../../../components/core";

import {
  LOCKER_INFO,
  LOCKER_TYPES,
  NEW_LOCKER_INFO_Q,
} from "../../../constants/graphql/locker-queries";
import {
  QUERY_LOCKER_NAME,
  QUERY_LOCKER_TYPE,
} from "../../../constants/graphql/drop-off-graphql";

import {
  clearData,
  getLockerTypeList,
  handleOnChanged,
  setSearchLockerData,
} from "../../../features/lockers/lockers-slice";
import { Card } from "primereact/card";
import { Strings } from "../../../constants/strings";

// Constants
const ALL_OPTION_VALUE = "-1";
const DEFAULT_OPERATOR_ID = 1; // Consider moving this to environment config

// Helper functions
const transformLockerData = (lockerInfo, selectedLockerName = null) => {
  return lockerInfo
    .filter(
      (element) =>
        !selectedLockerName ||
        selectedLockerName === ALL_OPTION_VALUE ||
        selectedLockerName == element.lockerId
    )
    .map((element) => ({
      lockerId: element.lockerId,
      lockerName: element.lockerName,
      lockerType: element.lockerType,
      lockerTypeId: element.lockerTypeId,
      serviceBy: element.partners.map((serviceElement) => ({
        companyNickname: serviceElement.companyNickname,
        partnerBranch: serviceElement.partnerBranch,
        agents: serviceElement.agents,
      })),
      doors: {
        occupiedDoors: element.occupiedDoors,
        availableDoors: element.availableDoors,
        unavailableDoors: element.unavailableDoors,
      },
      lockerStatus: element.lockerStatus,
      lockerStateNo: element.lockerStateNo,
    }));
};

const transformSelectOptions = (data, valueKey, nameKey) => {
  return data.map((element) => ({
    value: element[valueKey],
    name: element[nameKey],
  }));
};

const createSearchVariables = (operatorId, lockerTypeId) => {
  const variables = { operatorId };
  if (lockerTypeId !== ALL_OPTION_VALUE && lockerTypeId !== -1) {
    variables.lockerTypeId = lockerTypeId;
  }
  return variables;
};

export default function Lockers() {
  // Redux state
  const dispatch = useDispatch();
  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions
  );
  const lockerTypeList = useSelector((state) => state.lockers.lockerTypeList);
  const searchLockerFilter = useSelector(
    (state) => state.lockers.searchLockerFilter
  );
  const searchLockerData = useSelector(
    (state) => state.lockers.searchLockerData
  );

  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  // Local state
  const [selectedLockerType, setSelectedLockerType] =
    useState(ALL_OPTION_VALUE);
  const [selectedLockerName, setSelectedLockerName] =
    useState(ALL_OPTION_VALUE);
  const [isClicked, setIsClicked] = useState(false);
  // GraphQL queries
  const [lockerInfo, { refetch, data, loading }] = useLazyQuery(
    NEW_LOCKER_INFO_Q,
    {
      fetchPolicy: "no-cache",
    }
  );

  const { data: lockerTypesData } = useQuery(LOCKER_TYPES, {
    fetchPolicy: "no-cache",
  });

  const { data: lockerNameData } = useQuery(QUERY_LOCKER_NAME, {
    variables: {
      operatorId: DEFAULT_OPERATOR_ID,
      selectedLockerType: selectedLockerType,
    },
    skip: selectedLockerType === ALL_OPTION_VALUE,
  });

  const { data: lockerTypeData } = useQuery(QUERY_LOCKER_TYPE);

  // Memoized data transformations
  const lockerTypeOptions = useMemo(() => {
    if (!lockerTypeData?.lockerTypes) return [];
    return transformSelectOptions(
      lockerTypeData.lockerTypes,
      "lockerTypeId",
      "lockerType"
    );
  }, [lockerTypeData]);

  const lockerNameOptions = useMemo(() => {
    if (!lockerNameData?.lockers) return [];
    return transformSelectOptions(
      lockerNameData.lockers,
      "lockerId",
      "lockerName"
    );
  }, [lockerNameData]);

  // Event handlers
  const handleLockerTypeChange = useCallback((event) => {
    const newValue = event.target.value;
    setSelectedLockerType(newValue);
    setSelectedLockerName(ALL_OPTION_VALUE); // Reset locker name when type changes
  }, []);

  const handleLockerNameChange = useCallback((event) => {
    setSelectedLockerName(event.target.value);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedLockerType(ALL_OPTION_VALUE);
    setSelectedLockerName(ALL_OPTION_VALUE);
  }, []);

  const handleSearchLocker = useCallback(async () => {
    const variables = createSearchVariables(
      searchLockerFilter.operatorId,
      selectedLockerType
    );

    try {
      const result = await lockerInfo({ variables, fetchPolicy: "no-cache" });

      if (result.data?.lockerInfo) {
        const transformedData = transformLockerData(
          result.data.lockerInfo,
          selectedLockerName
        );
        dispatch(setSearchLockerData(transformedData));
      }
    } catch (error) {
      console.error("Error searching lockers:", error);
    }
  }, [
    lockerInfo,
    searchLockerFilter.operatorId,
    selectedLockerType,
    selectedLockerName,
    dispatch,
  ]);

  const handleRefetch = useCallback(async () => {
    const variables = createSearchVariables(
      searchLockerFilter.operatorId,
      searchLockerFilter.lockerTypeId
    );

    try {
      const result = await refetch(variables);

      if (result.data?.lockerInfo) {
        const transformedData = transformLockerData(result.data.lockerInfo);
        dispatch(setSearchLockerData(transformedData));
      }
    } catch (error) {
      console.error("Error refetching lockers:", error);
    }
  }, [refetch, searchLockerFilter, dispatch]);

  // Effects
  useEffect(() => {
    if (lockerTypesData?.lockerTypes) {
      const transformedTypes = transformSelectOptions(
        lockerTypesData.lockerTypes,
        "lockerTypeId",
        "lockerType"
      );
      dispatch(getLockerTypeList(transformedTypes));
    }
  }, [lockerTypesData, dispatch]);

  useEffect(() => {
    dispatch(clearData());
  }, [dispatch]);

  // Computed values
  const isLockerNameDisabled = selectedLockerType === ALL_OPTION_VALUE;
  const hasPermission =
    accessPermission.lockers === "1" || accessPermission.lockers === 1;

  const validateSearch = () => {
    setIsClicked(true);
    if (selectedLockerType !== "-1" && selectedLockerName === "-1") {
      setIsClicked(false);
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_error_title_search_criteria,
        message: Strings.popup_error_lockername,
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value="OK"
              className="p-2 w-full"
            />
          </div>
        ),
      });
      return;
    } else {
      handleSearchLocker();
    }
  };
  return (
    <ContentContainer containerTitle="Lockers">
      <div className="w-4/5 grid grid-cols-5 gap-2">
        {/* Locker Type Selection */}
        <div className="col-span-1">
          <p className="text-base mt-2">Locker Type:</p>
        </div>
        <div className="bg-slate-50 col-span-3">
          <Select
            items={lockerTypeOptions}
            defaultOption={{ value: ALL_OPTION_VALUE, name: "-- ALL --" }}
            value={selectedLockerType}
            onChange={handleLockerTypeChange}
          />
        </div>
        <div />

        {/* Locker Name Selection */}
        <div className="col-span-1">
          <p className="text-base mt-2">Locker Name:</p>
        </div>
        <div className="bg-slate-50 col-span-3">
          <Select
            items={lockerNameOptions}
            defaultOption={{ value: ALL_OPTION_VALUE, name: "-- ALL --" }}
            disable={isLockerNameDisabled}
            value={selectedLockerName}
            onChange={handleLockerNameChange}
          />
        </div>

        {/* Reset Button */}
        <div className="grid grid-cols-2 w-1/2 gap-y-2">
          <div />
          <button
            type="button"
            className="flex hover:text-blue-500 cursor-pointer mt-4 ml-auto w-min  underline"
            onClick={handleReset}
          >
            <i className="fas fa-undo my-auto" />
            <span className="ml-1">Reset</span>
          </button>
        </div>

        <div />

        {/* Search Button */}
        <div className="col-span-1 flex mt-4">
          <Button
            onClick={validateSearch}
            className="w-full h-full py-1"
            value="Search"
          />
        </div>
      </div>

      <hr className="mt-4" />

      {/* Loading Indicator */}
      <LinearProgressBar label="" open={loading} />

      {/* Results Table */}
      {data && isClicked && (
        <Card>
          <LockerTable
            disabled={hasPermission}
            items={searchLockerData}
            lockerTypeId={searchLockerFilter.lockerTypeId}
            refetch={handleRefetch}
          />
        </Card>
      )}

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </ContentContainer>
  );
}
